import { useMutation, useQueryClient } from '@tanstack/react-query';
import { operations } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import useDataDog from '@/reporting/DataDog';
import uploadDocumentViaS3 from './uploadDocumentViaS3';

type CreateCompanyDocumentBody = operations['createCompanyDocument']['requestBody']['content']['application/json'];
type CreateCompanyDocumentResponse =
  operations['createCompanyDocument']['responses'][201]['content']['application/json'];

export type CompanyDocumentType = CreateCompanyDocumentBody['documentType'];

/**
 * Uploads a company document (tax ID or articles of incorporation) end to end: registers the
 * document with SEA to get a presigned S3 URL, PUTs the file bytes directly to S3, then confirms
 * the upload. On failure, the caller re-triggers the mutation, which registers a fresh document
 * and upload URL from scratch.
 */
const useUploadCompanyDocument = (documentType: CompanyDocumentType) => {
  const { addError } = useDataDog();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['uploadCompanyDocument', documentType],
    mutationFn: async (file: File) => {
      const { documentUuid } = await uploadDocumentViaS3(
        file,
        async ({ fileName, contentType, contentSha256 }) => {
          try {
            const created = await apiClient
              .post('api/supplier-experience/companies/documents', {
                json: { documentType, fileName, contentType, contentSha256 } satisfies CreateCompanyDocumentBody,
              })
              .json<CreateCompanyDocumentResponse>();
            return created.data;
          } catch (error) {
            throw new Error(`Failed to register ${documentType} for upload`, { cause: error });
          }
        },
        async (documentUuid, size) => {
          try {
            await apiClient
              .post(`api/supplier-experience/companies/documents/${documentUuid}/complete`, { json: { size } })
              .json();
          } catch (error) {
            throw new Error(`Failed to confirm upload of ${documentType} [documentUuid=${documentUuid}]`, {
              cause: error,
            });
          }
        }
      );

      return { documentUuid, documentType };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyStatus'] });
    },
    onError: (error) => {
      addError('useUploadCompanyDocument', { error, documentType });
    },
  });
};

export default useUploadCompanyDocument;
